import React from "react";
import styles from "./index.module.scss";
import useFakeScrollbar from "../../hooks/useFakeScrollbar";

// Special case: one Excel file contains multiple questions
const TITLE2_8 = "2_8.請問您地震後住在避難地點幾天？";
const TITLE2_9_1 = "2_9.請問您地震後平均在避難地點住幾天？_四級震度";
const TITLE2_9_2 = "2_9.請問您地震後平均在避難地點住幾天？_五弱以上震度";
const TITLE4_2 = "4_2.家中「學齡兒童(6-12歲)」有幾人？";
const TITLE4_3 = "4_3.家中「75歲及以上老人」有幾人？";
const TITLE4_4 = "4_4.家中「身心障礙者(領有身心障礙手冊)」有幾人？";

const formatNumber = (num) => {
	let numValue = Number(num);
	if (Number.isNaN(numValue)) return num;
    // from 1562 => 1,562
	return numValue.toLocaleString();
};


const extractQuestionTitle = (rawTitle) => rawTitle.substring(4);

const TableHeadRow = ({ row, rowIndex }) => (
	<tr key={row[0] || rowIndex}>
		{row.map((cell, cellIndex) => {
			if (!cell && cellIndex > 0) return null;
			let colSpan = 1;
			let nextCellIndex = cellIndex + 1;
			while (cellIndex < row.length && row[nextCellIndex] === null) {
				colSpan++;
				nextCellIndex++;
			}
			return (
				<th colSpan={colSpan} key={rowIndex + cellIndex + cell}>
					{cell}
				</th>
			);
		})}
	</tr>
);

const TableBodyRow = ({
	row,
	rowIndex,
	columnIsPercentage,
	columnIsInteger,
}) => (
	<tr key={row[0] || rowIndex}>
		{row.map((cell, cellIndex) => {
			const numValue = Number(cell);
			let content = cell;
			if (!Number.isNaN(numValue)) {
				if (columnIsPercentage[cellIndex]) {
					content = `${(numValue * 100).toFixed(1)}%`;
				} else if (columnIsInteger[cellIndex]) {
					content = numValue.toFixed(1).toString();
				} else if (Number.isInteger(numValue)) {
					content = numValue.toString();
				} else {
					content = numValue.toFixed(2).toString();
				}
			}
			if (cell === null) {
				if (columnIsPercentage[cellIndex]) {
                    content = "0.0%";
                } else {
					content = "0";
				}
			}
			return (
				<td
					className={`${styles.tableData} ${
						!Number.isNaN(numValue) && styles.tableDataNumber
					}`}
					key={cellIndex + content}
				>
					{formatNumber(content)}
				</td>
			);
		})}
	</tr>
);

function DataTable({ tableData, topicKey }) {
	const {
		containerRef,
		thumbWidth,
		thumbLeft,
		showScrollbar,
		handleThumbPointerDown,
	} = useFakeScrollbar([tableData]);

	if (!Array.isArray(tableData) || tableData.length < 2) return;
	const colCount = tableData[0].length;
	const columnIsPercentage = new Array(colCount).fill(false);
	const columnIsInteger = new Array(colCount).fill(false);

	// 2-8 沒有「次數分配」、「有效百分比」列，做額外處理
	const isTopic2_8 = topicKey === extractQuestionTitle(TITLE2_8);

	// 2-9 需處理多餘的 null 值資料欄位
	const isTopic2_9 =
		topicKey === extractQuestionTitle(TITLE2_9_1) ||
		topicKey === extractQuestionTitle(TITLE2_9_2);

	const renderData = isTopic2_9
		? tableData.map((row) => [row[0], row[1]])
		: tableData;

	if (isTopic2_8) {
		columnIsPercentage[2] = true;
		columnIsPercentage[4] = true;
	}

	for (let i = 0; i < colCount; i++) {
		if (renderData[1].length - 1 >= i && typeof renderData[1][i] === "string") {
			if (renderData[1][i].includes("百分比")) {
				columnIsPercentage[i] = true;
			}
			if (renderData[1][i].includes("次數分配")) {
				columnIsInteger[i] = true;
			}
		}
	}

	const tableHeadLength = isTopic2_8 ? 1 : 2;

	const tableHeadData = renderData.slice(0, tableHeadLength);
	const tableBodyData = renderData.slice(tableHeadLength);

	return (
		<div className={styles.wrapper}>
			<div ref={containerRef} className={styles.tableContainer}>
				<table className={styles.table}>
					<thead className={styles.tableHead}>
						{tableHeadData.map((row, rowIndex) => (
							<TableHeadRow
								key={row[0] || rowIndex}
								row={row}
								rowIndex={rowIndex}
							/>
						))}
					</thead>
					<tbody>
						{tableBodyData.map((row, rowIndex) => (
							<TableBodyRow
								key={row[0] || rowIndex}
								row={row}
								rowIndex={rowIndex}
								columnIsPercentage={columnIsPercentage}
								columnIsInteger={columnIsInteger}
							/>
						))}
					</tbody>
				</table>
			</div>
			{showScrollbar && (
				<div className={styles.fakeScrollbar}>
					<div
						className={styles.fakeScrollbarThumb}
						onPointerDown={handleThumbPointerDown}
						style={{
							width: thumbWidth,
							transform: `translateX(${thumbLeft}px)`,
						}}
					/>
				</div>
			)}
		</div>
	);
}

function TopicDataTable({ tableData }) {
	const splitIndexes = [];
	const titles = [];

	for (let i = 0; i < tableData.length; i++) {
		if (tableData[i].length > 0) {
			if (
				tableData[i][0] === TITLE2_8 ||
				tableData[i][0] === TITLE2_9_1 ||
				tableData[i][0] === TITLE2_9_2 ||
				tableData[i][0] === TITLE4_2 ||
				tableData[i][0] === TITLE4_3 ||
				tableData[i][0] === TITLE4_4
			) {
				splitIndexes.push(i);
				titles.push(extractQuestionTitle(tableData[i][0]));
			}
		}
	}

	const firstRow = tableData[0];
	const firstRowTitle = typeof firstRow[0] === "string" && firstRow[0];

	if (splitIndexes.length === 0)
		return <DataTable tableData={tableData.slice(1)} />;

	titles.unshift(extractQuestionTitle(firstRowTitle));
	// 先加入第一個片段 (從開始到第一個分隔點)
	const tableSections = [tableData.slice(1, splitIndexes[0])];

	// 加入中間的片段 (每一個分隔點之間的資料)
	for (let i = 0; i < splitIndexes.length - 1; i++) {
		tableSections.push(
			tableData.slice(splitIndexes[i] + 1, splitIndexes[i + 1]),
		);
	}

	// 加入最後一個片段 (最後一個分隔點到結束)
	tableSections.push(
		tableData.slice(splitIndexes[splitIndexes.length - 1] + 1),
	);

	return (
		<div className={styles.tablesWrapper}>
			{tableSections.map((section, index) => (
				<React.Fragment key={titles[index]}>
					<h4 className={styles.tableTitle}>
						{titles[index].replaceAll("_", "")}
					</h4>
					<DataTable tableData={section} topicKey={titles[index]} />
				</React.Fragment>
			))}
		</div>
	);
}

export default TopicDataTable;
